import { AutocompleteFilters, FiltersArg } from "@/features/FilterTable/types";
import { getAuthorized } from "@/lib/fetch";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues } from "react-hook-form";
import { toast } from "sonner";

/**
 * Type for items returned by the autocomplete API
 */
export type AutocompleteItem = {
  id: number;
  text: string;
  [key: string]: any;
};

function normalizeFilters(filters?: AutocompleteFilters | undefined) {
  const clean = <T extends Record<string, any> | undefined>(obj: T) => {
    if (!obj) return undefined;
    const out: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      // drop undefined; keep null/empty-string (server decides)
      if (value !== undefined) out[key] = value;
    }
    return Object.keys(out).length ? out : undefined;
  };

  return {
    equals: clean(filters?.equals),
    contains: clean(filters?.contains),
  };
}

function buildFiltersKey(filters?: AutocompleteFilters) {
  const { equals, contains } = normalizeFilters(filters);
  // stable key for "did filters change"
  return JSON.stringify({ equals, contains });
}

/**
 * Hook for managing autocomplete items with infinite scroll
 *
 * @param base_url - Base URL for API requests
 * @param column_name - Column name to search in
 * @returns State and handlers for autocomplete items
 */
export function useAutocompleteItems<T extends FieldValues = any>(
  base_url: string,
  column_name: string,
  opts: {
    allowEmptyQuery?: boolean;
    pageSize?: number;
    keepDuplicates?: boolean;
    filters?: FiltersArg<T>;
  } = {},
) {
  const [items, setItems] = useState<AutocompleteItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const allowEmpty = opts.allowEmptyQuery ?? false;
  const limit = opts.pageSize ?? 60;
  const keepDuplicates =
    opts.keepDuplicates != undefined ? opts.keepDuplicates : true;

  // compute filters fresh on every request (so they follow form changes)
  const getFilters = useCallback((): AutocompleteFilters | undefined => {
    if (typeof opts.filters === "function") return opts.filters();
    return opts.filters;
  }, [opts.filters]);

  // we also keep a stable key to reset paging when filters change
  const filtersKey = useMemo(() => buildFiltersKey(getFilters()), [getFilters]);

  const abortRef = useRef<AbortController | null>(null);
  const delayRef = useRef<number | null>(null);
  const pagesLoadedRef = useRef(0);

  const clearDelay = () => {
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }
  };

  const cancelPending = useCallback(() => {
    abortRef.current?.abort();
    clearDelay();
  }, []);

  const loadItems = useCallback(
    async (currentOffset: number, currentQuery: string) => {
      if (!allowEmpty && (!currentQuery || currentQuery.length < 1)) return;

      // cancel any in-flight fetch before issuing a new one
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        setIsLoading(true);

        const filters = normalizeFilters(getFilters());
        const params = new URLSearchParams({
          query: currentQuery,
          offset: String(currentOffset),
          limit: String(limit),
        });

        if (filters.equals)
          params.set("equals", JSON.stringify(filters.equals));
        if (filters.contains)
          params.set("contains", JSON.stringify(filters.contains));

        const url = `${
          import.meta.env.VITE_BACKEND_URL
        }/${base_url}/autocomplete/${column_name}?${params.toString()}`;

        const res = await getAuthorized(url, controller.signal);

        if (!res.ok) {
          throw new Error("Network response was not ok");
        }

        let json: Array<AutocompleteItem> = await res.json();

        setHasMore(json.length === limit);

        if (!keepDuplicates) {
          const seen = new Set<string>();
          const unique: AutocompleteItem[] = [];
          for (const it of json) {
            const key = it.text;
            if (!seen.has(key)) {
              seen.add(key);
              unique.push(it);
            }
          }
          json = unique;
        }

        if (currentOffset === 0) {
          setItems(json);
        } else {
          setItems((prev) => {
            if (keepDuplicates) return [...prev, ...json];
            const seen = new Set(prev.map((it) => it.text));
            const added: AutocompleteItem[] = [];
            for (const it of json) {
              const k = it.text;
              if (!seen.has(k)) {
                seen.add(k);
                added.push(it);
              }
            }
            const next = added.length ? [...prev, ...added] : prev;
            if (added.length === 0 && json.length === limit) {
              queueMicrotask(() => onLoadMore?.());
            }
            return next;
          });
        }
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") {
        } else {
          toast.error(
            `Search error: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [allowEmpty, base_url, column_name, limit, getFilters],
  );

  const onLoadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    if (!allowEmpty && (!searchQuery || searchQuery.length < 1)) return;

    const next = offset + limit;
    setOffset(next);
    await loadItems(next, searchQuery);
  }, [isLoading, hasMore, allowEmpty, searchQuery, offset, limit, loadItems]);

  const onQueryChange = useCallback(
    (newQuery: string) => {
      if (newQuery === searchQuery && items.length > 0) return;
      setSearchQuery(newQuery);
      setOffset(0);
      setItems([]);
      pagesLoadedRef.current = 0;
      setHasMore(true);

      if ((allowEmpty && newQuery.length >= 0) || newQuery.length >= 1) {
        loadItems(0, newQuery);
      }
    },
    [allowEmpty, loadItems],
  );

  // If filters change, reset paging & refetch for current query
  useEffect(() => {
    setOffset(0);
    setItems([]);
    setHasMore(true);

    if ((allowEmpty && searchQuery.length >= 0) || searchQuery.length >= 1) {
      loadItems(0, searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => () => cancelPending(), [cancelPending]);

  return {
    items,
    hasMore,
    isLoading,
    onLoadMore,
    onQueryChange,
    searchQuery,
    offset,
    cancelPending,
  };
}
